import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, Observer, of } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { pluck } from '../../../../helpers/utils';
import { ScActionColumn, ScGridContent, ScTableActionSelection } from '../../../../models/table.model';
import { ScDialogService } from '../../../sc-dialogs';

@Component({
  selector: 'sc-image-grid',
  templateUrl: './sc-image-grid.component.html',
  styleUrls: ['./sc-image-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ScImageGridComponent implements OnInit, OnChanges {
  @Input() items: any[];

  @Output() actionSelection: EventEmitter<ScTableActionSelection> = new EventEmitter();

  public actions: ScActionColumn;
  public gridProps: ScGridContent;
  public data$: Observable<any[]>;
  public loading: boolean;

  private _data$: BehaviorSubject<any[]> = new BehaviorSubject([]);

  constructor(private _dialogService: ScDialogService, private _cd: ChangeDetectorRef, @Inject('env') private _env: any) {}

  @Input() set data(data: any[]) {
    this._data$.next(data);
  }

  openDialog(startIndex: number, urls: any[]) {
    urls = urls.map(({ url }) => ({
      small: url,
      medium: url,
      big: url,
      url
    }));

    this._dialogService.imagePreviewer(urls, startIndex);
  }

  public trackByIndex = (index: number): number => index;

  ngOnInit() {
    this.data$ = this._data$.asObservable().pipe(
      filter(data => data.length > 0),
      switchMap(data => {
        this.loading = true;
        const validateImages = data.map((item: ScGridContent) => {
          const blob = pluck(item, this.gridProps.blobSrc);
          const link = pluck(item, this.gridProps.linkSrc);
          let linkUrl = '';
          let blobUrl = '';

          if (link) {
            linkUrl = `${this._env.apiURL}/Files/GetFile/${link}`;
          }

          if (blob) {
            blobUrl = `data:image/png;base64,${blob}`;
          }

          return this.getBase64FromImageUrl(linkUrl).pipe(
            switchMap(url => {
              if (url === 'invalid' && blobUrl) {
                return this.getBase64FromImageUrl(blobUrl);
              }
              return of(url);
            }),
            map(url => {
              if (url === 'invalid') {
                return '/assets/images/image-solid.svg';
              }
              return url;
            })
          );
        });
        return combineLatest([...validateImages]).pipe(
          map((validatedImages: string[]) =>
            validatedImages.map((url: string, index: number) => {
              const imageData = {
                ...data[index],
                url
              };
              return imageData;
            })
          )
        );
      }),
      tap(() => {
        this.loading = false;
        this._cd.markForCheck();
      })
    );
  }
  getBase64FromImageUrl(url: string) {
    return new Observable((observer: Observer<string>) => {
      if (!url) {
        return observer.next('invalid');
      }
      const img = new Image();

      img.setAttribute('crossOrigin', 'anonymous');

      img.onload = () => {
        observer.next(img.src);
      };

      img.onerror = () => {
        observer.next('invalid');
      };

      img.src = url;
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.items && changes.items.currentValue && changes.items.currentValue !== changes.items.previousValue) {
      const [gridProps, actions] = changes.items.currentValue;
      this.gridProps = gridProps;
      this.actions = actions;
    }
  }
}
